import React, { useState, useEffect, useCallback } from 'react';
import { formatTier } from "../lib/tierLabel";
import { toast } from 'sonner';
import {
  Loader2, ArrowRight, AlertTriangle, CheckCircle2, TrendingUp,
  TrendingDown, Minus, Shield, Zap, BarChart3, Activity,
  ChevronDown, ChevronUp, FlaskConical
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Slider } from './ui/slider';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { useAuth } from '../context/AuthContext';

const TIER_COLORS = {
  tier_1: { bg: 'bg-red-100', text: 'text-red-800', label: 'Tier 1 — Critical' },
  tier_2: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Tier 2 — High' },
  tier_3: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Tier 3 — Medium' },
  tier_4: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Tier 4 — Low' },
};

export default function ImpactSimulator() {
  const { api } = useAuth();
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProcessId, setSelectedProcessId] = useState('');
  const [simRto, setSimRto] = useState(24);
  const [simRpo, setSimRpo] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  const fetchProcesses = useCallback(async () => {
    try {
      const res = await api.get('/inheritance/simulate/processes');
      setProcesses(res.data.processes || []);
    } catch { toast.error('Failed to load processes'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchProcesses(); }, [fetchProcesses]);

  const selectedProcess = processes.find(p => p.id === selectedProcessId);

  useEffect(() => {
    if (selectedProcess) {
      setSimRto(selectedProcess.current_rto || 24);
      setSimRpo(selectedProcess.current_rpo || null);
      setResult(null);
    }
  }, [selectedProcessId, selectedProcess]);

  const handleSimulate = async () => {
    if (!selectedProcessId) return;
    setSimulating(true);
    try {
      const body = { simulated_rto_hours: simRto };
      if (simRpo !== null) body.simulated_rpo_hours = simRpo;
      const res = await api.post(`/inheritance/simulate/${selectedProcessId}`, body);
      setResult(res.data);
    } catch { toast.error('Simulation failed'); }
    finally { setSimulating(false); }
  };

  const toggle = (section) => setExpandedSection(expandedSection === section ? null : section);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[30vh]" data-testid="simulator-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="impact-simulator">
      {/* Header */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3 bg-gradient-to-r from-indigo-950 to-slate-900">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <FlaskConical className="w-5 h-5" />
            Dependency Impact Simulator
          </CardTitle>
          <p className="text-sm text-slate-300">
            Preview cascade impact before committing RTO/RPO changes — no data is modified
          </p>
        </CardHeader>
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            {/* Process Selector */}
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Business Process</label>
              <Select value={selectedProcessId} onValueChange={setSelectedProcessId}>
                <SelectTrigger data-testid="simulator-process-select">
                  <SelectValue placeholder="Select a process..." />
                </SelectTrigger>
                <SelectContent>
                  {processes.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="font-medium">{p.process_code}</span>
                      <span className="text-slate-500 ml-2">{p.process_name}</span>
                      <span className="text-xs text-slate-400 ml-2">({p.dependent_count} deps)</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* RTO Slider */}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
                Simulated RTO: <span className="text-sky-700">{simRto}h</span>
                {selectedProcess && (
                  <span className="text-slate-400 ml-1">(current: {selectedProcess.current_rto}h)</span>
                )}
              </label>
              <Slider
                value={[simRto]}
                onValueChange={(v) => setSimRto(v[0])}
                min={1}
                max={336}
                step={1}
                disabled={!selectedProcessId}
                data-testid="simulator-rto-slider"
              />
            </div>

            {/* Run Button */}
            <div>
              <Button
                onClick={handleSimulate}
                disabled={!selectedProcessId || simulating}
                className="w-full bg-sky-700 hover:bg-sky-800"
                data-testid="simulate-btn"
              >
                {simulating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                Run Simulation
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-4" data-testid="simulation-results">
          {/* Delta Overview */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {/* RTO Change */}
            <Card className={`${result.rto_delta < 0 ? 'border-red-200 bg-red-50/30' : result.rto_delta > 0 ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'}`}>
              <CardContent className="py-4 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  {result.rto_delta < 0 ? <TrendingDown className="w-4 h-4 text-red-600" /> :
                   result.rto_delta > 0 ? <TrendingUp className="w-4 h-4 text-emerald-600" /> :
                   <Minus className="w-4 h-4 text-slate-400" />}
                  <span className={`text-lg font-bold ${result.rto_delta < 0 ? 'text-red-700' : result.rto_delta > 0 ? 'text-emerald-700' : 'text-slate-600'}`}>
                    {result.rto_delta > 0 ? '+' : ''}{result.rto_delta}h
                  </span>
                </div>
                <p className="text-[10px] text-slate-500">RTO Change</p>
              </CardContent>
            </Card>

            {/* Tier Change */}
            <Card className={result.tier_changed ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200'}>
              <CardContent className="py-4 text-center">
                <div className="flex items-center justify-center gap-1 mb-1 flex-wrap">
                  <Badge className={`text-[10px] ${TIER_COLORS[result.current.tier]?.bg} ${TIER_COLORS[result.current.tier]?.text}`}>
                    {formatTier(result.current.tier)}
                  </Badge>
                  {result.tier_changed && (
                    <>
                      <ArrowRight className="w-3 h-3 text-slate-400" />
                      <Badge className={`text-[10px] ${TIER_COLORS[result.simulated.tier]?.bg} ${TIER_COLORS[result.simulated.tier]?.text}`}>
                        {formatTier(result.simulated.tier)}
                      </Badge>
                    </>
                  )}
                </div>
                <p className="text-[10px] text-slate-500">{result.tier_changed ? 'Tier Change' : 'Tier Unchanged'}</p>
              </CardContent>
            </Card>

            {/* Affected */}
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-lg font-bold text-slate-800">{result.summary.total_affected_assets}</p>
                <p className="text-[10px] text-slate-500">Assets Affected</p>
              </CardContent>
            </Card>

            {/* New Violations */}
            <Card className={result.summary.new_violations_count > 0 ? 'border-red-200 bg-red-50/30' : 'border-slate-200'}>
              <CardContent className="py-4 text-center">
                <p className={`text-lg font-bold ${result.summary.new_violations_count > 0 ? 'text-red-700' : 'text-slate-600'}`}>
                  {result.summary.new_violations_count}
                </p>
                <p className="text-[10px] text-slate-500">New Violations</p>
              </CardContent>
            </Card>

            {/* Resolved */}
            <Card className={result.summary.resolved_violations_count > 0 ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'}>
              <CardContent className="py-4 text-center">
                <p className={`text-lg font-bold ${result.summary.resolved_violations_count > 0 ? 'text-emerald-700' : 'text-slate-600'}`}>
                  {result.summary.resolved_violations_count}
                </p>
                <p className="text-[10px] text-slate-500">Resolved</p>
              </CardContent>
            </Card>
          </div>

          {/* Recommendation */}
          <Card className="border-sky-200 bg-sky-50/40">
            <CardContent className="py-4 flex items-start gap-3">
              <Shield className="w-5 h-5 text-sky-700 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-indigo-800">AI Recommendation</p>
                <p className="text-sm text-sky-700 mt-0.5">{result.summary.recommendation}</p>
              </div>
            </CardContent>
          </Card>

          {/* Violations / Resolved */}
          {(result.new_violations.length > 0 || result.resolved_violations.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {result.new_violations.length > 0 && (
                <Card className="border-red-200">
                  <CardContent className="py-4">
                    <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" /> New Violations
                    </h4>
                    {result.new_violations.map((v, i) => (
                      <div key={i} className="text-xs text-red-700 bg-red-50 rounded p-2 mb-1">
                        <span className="font-semibold">{v.type}</span>: {v.detail}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              {result.resolved_violations.length > 0 && (
                <Card className="border-emerald-200">
                  <CardContent className="py-4">
                    <h4 className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" /> Resolved Violations
                    </h4>
                    {result.resolved_violations.map((v, i) => (
                      <div key={i} className="text-xs text-emerald-700 bg-emerald-50 rounded p-2 mb-1">
                        <span className="font-semibold">{v.type}</span>: {v.detail}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Expandable Detail Tables */}
          {result.affected_assets.length > 0 && (
            <Card>
              <button
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
                onClick={() => toggle('assets')}
                data-testid="expand-assets"
              >
                <span className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-600" />
                  Affected Assets ({result.affected_assets.length})
                </span>
                {expandedSection === 'assets' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {expandedSection === 'assets' && (
                <CardContent className="pt-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Asset</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Current RTO</TableHead>
                        <TableHead>Simulated RTO</TableHead>
                        <TableHead>Change</TableHead>
                        <TableHead>Tier</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.affected_assets.map((a, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium text-sm">{a.asset_name}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{a.asset_type}</Badge></TableCell>
                          <TableCell>{a.current_rto}h</TableCell>
                          <TableCell className="font-semibold">{a.simulated_rto}h</TableCell>
                          <TableCell className={a.rto_change < 0 ? 'text-red-600 font-semibold' : a.rto_change > 0 ? 'text-emerald-600 font-semibold' : ''}>
                            {a.rto_change > 0 ? '+' : ''}{a.rto_change}h
                          </TableCell>
                          <TableCell>
                            {a.tier_changed ? (
                              <div className="flex items-center gap-1">
                                <Badge className={`text-[10px] ${TIER_COLORS[a.current_tier]?.bg} ${TIER_COLORS[a.current_tier]?.text}`}>
                                  {formatTier(a.current_tier)}
                                </Badge>
                                <ArrowRight className="w-3 h-3" />
                                <Badge className={`text-[10px] ${TIER_COLORS[a.simulated_tier]?.bg} ${TIER_COLORS[a.simulated_tier]?.text}`}>
                                  {formatTier(a.simulated_tier)}
                                </Badge>
                              </div>
                            ) : <span className="text-xs text-slate-400">No change</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              )}
            </Card>
          )}

          {result.affected_recovery_tasks.length > 0 && (
            <Card>
              <button
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
                onClick={() => toggle('recovery')}
                data-testid="expand-recovery"
              >
                <span className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-600" />
                  Affected Recovery Tasks ({result.affected_recovery_tasks.length})
                </span>
                {expandedSection === 'recovery' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {expandedSection === 'recovery' && (
                <CardContent className="pt-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Task Type</TableHead>
                        <TableHead>Planned RTO</TableHead>
                        <TableHead>Current Inherited RTO</TableHead>
                        <TableHead>Simulated Inherited RTO</TableHead>
                        <TableHead>Current Violation</TableHead>
                        <TableHead>Simulated Violation</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.affected_recovery_tasks.map((t, i) => (
                        <TableRow key={i} className={t.simulated_violation ? 'bg-red-50/50' : ''}>
                          <TableCell className="font-medium text-sm">{t.task_type}</TableCell>
                          <TableCell>{t.planned_rto ?? '-'}h</TableCell>
                          <TableCell>{t.current_inherited_rto}h</TableCell>
                          <TableCell className="font-semibold">{t.simulated_inherited_rto}h</TableCell>
                          <TableCell>
                            {t.current_violation
                              ? <Badge className="bg-red-100 text-red-700 text-[10px]">Violation</Badge>
                              : <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">OK</Badge>}
                          </TableCell>
                          <TableCell>
                            {t.simulated_violation
                              ? <Badge className="bg-red-100 text-red-700 text-[10px]">Violation</Badge>
                              : <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">OK</Badge>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
